import { useCallback, useReducer } from "react";

import { GameHistory } from "../api/gameHistory";
import { GameStreamEvent } from "../api/gameStream";
import { TeamSide } from "../constants/teams";
import { Activity, GameFullResponse, LineUpPlayer } from "../types/gameFull";
import { GameRosters } from "../types/roster";
import { LineupDraft } from "../utils/lineupDraft";
import { GameStateEdit, PitchTags } from "../utils/pitchTagging";
import {
  PlateAppearanceEditValues,
  PlateAppearancePlayers,
} from "../utils/plateAppearanceEditor";
import { getDefaultSelectionId } from "../utils/plateAppearanceFeed";
import { PlayerEdit } from "../utils/playerEdit";
import { scorekeepingReducer } from "./scorekeepingReducer";

/**
 * Game data + pitch selection for Scorekeeping V2. Hydrated from the game
 * service /full snapshot (`hydrate`) and kept current by its WebSocket
 * (`receiveStreamEvent`); edits apply locally on top of it.
 */
export const useScorekeepingGame = (
  initialData: GameFullResponse,
  initialRosters: GameRosters,
) => {
  const [state, dispatch] = useReducer(
    scorekeepingReducer,
    initialData,
    (data) => ({
      data,
      rosters: initialRosters,
      selectedId: getDefaultSelectionId(data),
      inningMoves: {},
      isHistoryLoaded: false,
    }),
  );

  /** Replaces the game data with a game service snapshot */
  const hydrate = useCallback(
    (data: GameFullResponse) => dispatch({ type: "hydrate", data }),
    [],
  );

  /** Adds the pitches and at bats older than the snapshot's */
  const mergeHistory = useCallback(
    (history: GameHistory) => dispatch({ type: "mergeHistory", history }),
    [],
  );

  /** Applies a game service WebSocket event */
  const receiveStreamEvent = useCallback(
    (event: GameStreamEvent) => dispatch({ type: "streamEvent", event }),
    [],
  );

  /** Replaces the rosters, e.g. when the omnibus rosters (re)load */
  const setRosters = useCallback(
    (rosters: GameRosters) => dispatch({ type: "setRosters", rosters }),
    [],
  );

  return {
    data: state.data,
    isHistoryLoaded: state.isHistoryLoaded,
    mergeHistory,
    rosters: state.rosters,
    selectedId: state.selectedId,
    selectedPlateAppearanceId: state.selectedPlateAppearanceId,
    select: (id: string) => dispatch({ type: "select", id }),
    selectPlateAppearance: (plateAppearanceId: string) =>
      dispatch({ type: "selectPlateAppearance", plateAppearanceId }),
    editPlateAppearance: (
      plateAppearanceId: string,
      values: PlateAppearanceEditValues,
    ) => dispatch({ type: "editPlateAppearance", plateAppearanceId, values }),
    editPlayer: (playerId: string, edit: PlayerEdit) =>
      dispatch({ type: "editPlayer", playerId, edit }),
    setJerseyNumber: (playerId: string, jerseyNumber: string) =>
      dispatch({ type: "setJerseyNumber", playerId, jerseyNumber }),
    applyLineups: (draft: LineupDraft) =>
      dispatch({ type: "applyLineups", draft }),
    substitutePlayer: (
      side: TeamSide,
      outPlayerId: string,
      inPlayer: LineUpPlayer,
      fallback?: Pick<LineUpPlayer, "battingOrder" | "position">,
    ) =>
      dispatch({
        type: "substitutePlayer",
        side,
        outPlayerId,
        inPlayer,
        fallback,
      }),
    addRosterPlayers: (side: TeamSide, players: LineUpPlayer[]) =>
      dispatch({ type: "addRosterPlayers", side, players }),
    hydrate,
    receiveStreamEvent,
    setRosters,
    /**
     * Active at-bat Change Hitter: frontend only. The next tagged pitch
     * carries the new hitter to omnibus, and lineup advancement follows it.
     */
    setPlateAppearancePlayers: (
      plateAppearanceId: string,
      players: PlateAppearancePlayers,
    ) =>
      dispatch({
        type: "setPlateAppearancePlayers",
        plateAppearanceId,
        players,
      }),
    setActiveHitter: (player: LineUpPlayer) =>
      dispatch({ type: "setActiveHitter", player }),
    /** Applies saved tags; the game service plays out the at bat */
    saveTags: (activityId: string, tags: PitchTags) =>
      dispatch({ type: "saveTags", activityId, tags }),
    /** Puts back a pitch's tags after its save failed */
    revertTags: (previous: Activity) =>
      dispatch({ type: "revertTags", previous }),
    editGameState: (edit: GameStateEdit) =>
      dispatch({ type: "editGameState", edit }),
  };
};
